import { DecorationSet } from '@codemirror/view';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@langchain/untitled-ui-icons';
import Stack from '@mui/joy/Stack';
import { SxProps } from '@mui/joy/styles/types';

import { forwardRef, useMemo, useRef, useState } from 'react';

import { CodeData } from '@/components/Code/Code';
import Header from '@/components/CodeHeader';
import { EditableChatInput } from '@/components/EditableChatInput';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { IFrameTraceRenderer } from '@/components/IFrameTraceRenderer';
import { Message } from '@/components/Message';
import { FancyRenderingProvider } from '@/components/RichTextEditor/utils/fancyRenderContext';
import { ButtonGroup, IconButton } from '@/design-system';
import { useIFrameTraceRendering } from '@/hooks/useIFrameTraceRendering';
import {
  ExampleSchema,
  MessageUnionType,
  RunOutputSchema,
  RunSchema,
} from '@/types/schema';
import {
  getFlattenedArrayOfGenerations,
  getMessageContent,
  getMessageFields,
  getMessageType,
  maybeGetArrayOfMessages,
  maybeGetOpenAIJSInputMessages,
  setMessageContent,
  setMessageFields,
  setMessageType,
  updateMessageInOutputs,
} from '@/utils/messages';
import { cn } from '@/utils/tailwind';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { ErrorBoundaryRawFallback } from '../../../components/ErrorBoundaryRawFallback';
import cs from '../styles.module.css';
import { RunDisplaySchema } from '../utils/types';
import {
  getOutputMessagesWithIndex,
  showChatMessageComponents,
} from '../utils/utils';
import { Skeleton } from './InputOutputLoadingSkeleton';
import { RunDetailsFunctionsAndStructuredOutput } from './RunDetailsFunctions';
import { RunDetailsHeaderToggle } from './RunDetailsHeaderToggle';
import { RunDetailsIO } from './RunDetailsIO';
import { RunOutput } from './RunOutput';
import { RunPageMessages } from './RunPageMessages';

interface PromptViewProps {
  input: string;
  output: string;
  sx?: SxProps;
  className?: string;
}

export const PromptView = forwardRef<HTMLDivElement, PromptViewProps>(
  ({ input, output, className }, ref) => {
    return (
      <div
        className={cn(
          'overflow-x-clip text-ellipsis whitespace-pre-wrap rounded-lg border border-secondary p-4 break-anywhere',
          className
        )}
        ref={ref}
      >
        {input}
        <span className="bg-green-500/30">{output}</span>
      </div>
    );
  }
);

export const RunDetailsLLM = ({
  run,
  prevRun,
  variant,
  exampleOutput,
  getDiffValues,
  forceRawRender,
  onEditInputs,
  onEditOutputs,
  inputsRef,
  outputsRef,
  inputSchema,
  outputSchema,
  inputsLoading,
  outputsLoading,
  errorLoading,
  showExpandAll,
}: {
  run: RunDisplaySchema;
  prevRun?: RunSchema | undefined;
  variant?: 'run' | 'plain' | 'example';
  exampleOutput?: ExampleSchema['outputs'];
  forceRawRender?: 'raw' | 'rendered';
  getDiffValues?: (type: 'inputs' | 'outputs') =>
    | {
        decoration: DecorationSet;
        generatedCode: string | undefined;
      }
    | undefined;
  onEditInputs?: (inputs: RunSchema['inputs']) => void;
  onEditOutputs?: (outputs: RunSchema['outputs']) => void;
  inputsRef?: React.RefObject<HTMLDivElement>;
  outputsRef?: React.RefObject<HTMLDivElement>;
  inputSchema?: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;
  inputsLoading?: boolean;
  outputsLoading?: boolean;
  errorLoading?: boolean;
  showExpandAll?: boolean;
}) => {
  const { inputs, outputs, error } = run;

  const { currIFrameConfig } = useIFrameTraceRendering(
    run.reference_dataset_id
  );

  const [_rawOutput, setRawOutput] = useLocalStorageState(
    'ls:runDetails:rawOuptut',
    false
  );

  const rawOutput =
    forceRawRender === 'raw'
      ? true
      : forceRawRender === 'rendered'
      ? false
      : _rawOutput;

  const inputDiffs = useMemo(() => getDiffValues?.('inputs'), [getDiffValues]);
  const outputDiffs = useMemo(
    () => getDiffValues?.('outputs'),
    [getDiffValues]
  );

  const [outputIndex, setOutputIndex] = useState(0);
  const llmTextRef = useRef<HTMLDivElement>(null);
  const isChatModel = Boolean(inputs?.messages);

  const inputMessages = maybeGetArrayOfMessages(inputs?.messages);

  const generations = getFlattenedArrayOfGenerations(outputs);

  const outputMessagesWithIndex = getOutputMessagesWithIndex(generations);

  const outputMessages = outputMessagesWithIndex?.map(
    (m) => m?.[0] as MessageUnionType
  );

  const input: string | undefined = inputs?.prompt ?? inputs?.prompts?.[0];
  const output: string | undefined = generations[outputIndex]?.text;
  const outputMessage = outputMessages?.[outputIndex];

  const onEditOutputMessage =
    onEditOutputs && outputs
      ? (newMessage: MessageUnionType) => {
          const updatedOutputs = updateMessageInOutputs(
            outputs,
            outputIndex,
            newMessage
          );
          onEditOutputs?.(updatedOutputs);
        }
      : undefined;

  if (showChatMessageComponents(inputs)) {
    const handleEdit = (messageSetter) =>
      onEditOutputMessage
        ? (value) => {
            const editedMessage = messageSetter(outputMessage, value);
            editedMessage && onEditOutputMessage(editedMessage);
          }
        : undefined;
    if (variant === 'plain') {
      return (
        <>
          <div
            className="mr-auto flex max-w-full flex-col items-start gap-4 pr-12 has-[[data-virtuoso-scroller]]:w-full"
            ref={inputsRef}
          >
            {inputsLoading ? (
              <Skeleton />
            ) : (
              <RunPageMessages
                messages={inputMessages ?? []}
                attachments={run.inputs_s3_urls}
                foldDistractions={{ prevRun }}
                mergeProps={(_, index) => ({
                  testid: `run-input-component-chat-${index}`,
                })}
                onEdit={
                  onEditInputs
                    ? (input: MessageUnionType[]) => {
                        onEditInputs({ ...inputs, messages: input });
                      }
                    : undefined
                }
              />
            )}
          </div>
          <div
            className="ml-auto flex max-w-full flex-col items-end gap-4 pl-12"
            ref={outputsRef}
          >
            <ExpandableErrorAlert error={error} />
            {outputsLoading || errorLoading ? (
              <Skeleton />
            ) : currIFrameConfig?.enabled &&
              outputs &&
              forceRawRender !== 'raw' ? (
              <IFrameTraceRenderer
                payload={outputs}
                config={currIFrameConfig}
                type="output"
                key={`${run.id}-output`}
              />
            ) : outputMessage ? (
              <Message
                testid={`run-output-component-chat`}
                role={getMessageType(outputMessage) ?? 'AI'}
                content={getMessageContent(outputMessage) ?? ''}
                fields={getMessageFields(outputMessage)}
                attachments={run.outputs_s3_urls}
                onEditContent={handleEdit(setMessageContent)}
                onEditFields={handleEdit(setMessageFields)}
                onChangeRole={handleEdit(setMessageType)}
                headerRight={
                  outputMessages.length > 1 && (
                    <ButtonGroup size="md">
                      <IconButton
                        icon={ChevronLeftIcon}
                        label="Previous output"
                        disabled={outputIndex === 0}
                        onClick={() => setOutputIndex((index) => index - 1)}
                      />
                      <IconButton
                        icon={ChevronRightIcon}
                        label="Next output"
                        disabled={outputIndex === outputMessages.length - 1}
                        onClick={() => setOutputIndex((index) => index + 1)}
                      />
                    </ButtonGroup>
                  )
                }
              />
            ) : (
              <RunOutput
                outputs={outputs}
                attachments={run.outputs_s3_urls}
                onEditOutputs={onEditOutputs}
                runType={run.run_type}
              />
            )}
          </div>
        </>
      );
    }

    return (
      <>
        {/* TODO: Make this function component editable */}
        <RunDetailsFunctionsAndStructuredOutput run={run} outputs={outputs} />

        <div
          className="border-b border-tertiary p-4 last-of-type:border-b-0"
          ref={inputsRef}
        >
          <FancyRenderingProvider>
            <RunDetailsLLMInput
              inputs={inputs}
              inputsLoading={inputsLoading}
              inputDiffs={inputDiffs}
              forceRawRender={forceRawRender}
              run={run}
              onEditInputs={onEditInputs}
              schema={inputSchema}
            />
          </FancyRenderingProvider>
        </div>

        <div
          className="border-b border-tertiary p-4 last-of-type:border-b-0"
          ref={outputsRef}
        >
          <FancyRenderingProvider>
            <RunDetailsLLMOutput
              run={run}
              outputs={outputs}
              outputsLoading={outputsLoading}
              outputIndex={outputIndex}
              setOutputIndex={setOutputIndex}
              forceRawRender={forceRawRender}
              diffValues={outputDiffs}
              onEditOutputs={onEditOutputs}
              rawOutput={rawOutput}
              setRawOutput={setRawOutput}
              schema={outputSchema}
            />
          </FancyRenderingProvider>
        </div>
        {exampleOutput && (
          <div className="border-b border-tertiary p-4 last-of-type:border-b-0">
            <Header text="Reference Output" />

            {currIFrameConfig?.enabled && !rawOutput ? (
              <IFrameTraceRenderer
                payload={exampleOutput.output}
                config={currIFrameConfig}
                type="reference"
                key={`${run.id}-reference`}
              />
            ) : getMessageType(exampleOutput.output) &&
              getMessageContent(exampleOutput.output) ? (
              <Message
                role={getMessageType(exampleOutput.output)}
                content={getMessageContent(exampleOutput.output) ?? ''}
              />
            ) : (
              <Message content="" kwargs={exampleOutput} />
            )}
          </div>
        )}
      </>
    );
  } else if (!isChatModel && input !== undefined && output !== undefined) {
    if (variant === 'plain') {
      return inputsLoading || outputsLoading ? (
        <Skeleton />
      ) : (
        <div
          className="flex flex-col gap-4"
          data-testid="llm-input-output-view"
        >
          {/* TODO: Make this prompt/completion component editable */}
          <PromptView ref={llmTextRef} input={input} output={output} />
        </div>
      );
    }

    const generations = getFlattenedArrayOfGenerations(outputs);

    return (
      <>
        <div className="border-b border-tertiary p-4 last-of-type:border-b-0">
          <Header
            text={
              <>
                Prompt & <span className={cs.green}>Completion</span>
              </>
            }
            right={
              (generations.length ?? 0) > 1 ? (
                <ButtonGroup size="md">
                  <IconButton
                    icon={ChevronLeftIcon}
                    label="Previous output"
                    disabled={outputIndex === 0}
                    onClick={() => setOutputIndex((index) => index - 1)}
                  />
                  <IconButton
                    icon={ChevronRightIcon}
                    label="Next output"
                    disabled={outputIndex === (generations.length ?? 0) - 1}
                    onClick={() => setOutputIndex((index) => index + 1)}
                  />
                </ButtonGroup>
              ) : null
            }
            copy={input + output}
            bodyRef={llmTextRef}
            withJumpButton={(input + output).length > 500}
          />
          <div
            className="flex flex-col gap-4"
            data-testid="llm-input-output-view"
          >
            {inputsLoading || outputsLoading ? (
              <Skeleton />
            ) : currIFrameConfig?.enabled &&
              outputs &&
              forceRawRender !== 'raw' ? (
              <>
                <PromptView ref={llmTextRef} input={input} output={''} />
                <IFrameTraceRenderer
                  payload={outputs}
                  config={currIFrameConfig}
                  type="output"
                  key={`${run.id}-output`}
                />
              </>
            ) : (
              <PromptView ref={llmTextRef} input={input} output={output} />
            )}
          </div>
        </div>

        {exampleOutput && (
          <div className="border-b border-tertiary p-4 last-of-type:border-b-0">
            <Header text="Reference Output" />

            {currIFrameConfig?.enabled && !rawOutput ? (
              <IFrameTraceRenderer
                payload={exampleOutput}
                config={currIFrameConfig}
                type="reference"
                key={`${run.id}-reference`}
              />
            ) : (
              <Message content="" kwargs={exampleOutput} />
            )}
          </div>
        )}
      </>
    );
  }
  // Fallback if other values are undefined
  return (
    <RunDetailsIO
      run={run}
      forceRawRender={forceRawRender}
      exampleOutput={exampleOutput}
      getDiffValues={getDiffValues}
      onEditInputs={onEditInputs}
      onEditOutputs={onEditOutputs}
      inputsRef={inputsRef}
      outputsRef={outputsRef}
      inputsLoading={inputsLoading}
      outputsLoading={outputsLoading}
      errorsLoading={errorLoading}
      showExpandAll={showExpandAll}
    />
  );
};

export const RunDetailsLLMInput = ({
  inputs,
  inputsLoading,
  inputDiffs,
  forceRawRender,
  run,
  onEditInputs,
  hideHeaders,
  schema,
}: {
  inputs: RunSchema['inputs'];
  inputsLoading?: boolean;
  inputDiffs?: {
    decoration: DecorationSet;
    generatedCode: string | undefined;
  };
  forceRawRender?: 'raw' | 'rendered';
  run: RunDisplaySchema;
  onEditInputs?: (inputs: RunSchema['inputs']) => void;
  hideHeaders?: boolean;
  schema?: Record<string, unknown>;
}) => {
  const { currIFrameConfig } = useIFrameTraceRendering(
    run.reference_dataset_id
  );

  const [_rawInput, setRawInput] = useLocalStorageState(
    'ls:runDetails:rawInput',
    false
  );
  const rawInput =
    forceRawRender === 'raw'
      ? true
      : forceRawRender === 'rendered'
      ? false
      : _rawInput;

  const inputMessages =
    maybeGetArrayOfMessages(inputs?.messages) ??
    maybeGetOpenAIJSInputMessages(inputs);

  if (!inputMessages) {
    return null;
  }

  const handleEditInputMessage = (messageSetter, index) =>
    onEditInputs &&
    ((value) => {
      const newMessages = [...inputMessages];
      newMessages[index] =
        messageSetter(inputMessages[index], value) ?? newMessages[index];
      onEditInputs({ ...inputs, messages: newMessages });
    });

  return (
    <>
      {!hideHeaders && (
        <RunDetailsHeaderToggle
          variant="input"
          raw={rawInput}
          onRawChange={setRawInput}
          disabled={!!inputDiffs}
          disabledMessage="Turn off diff mode to change input view"
          readOnly={forceRawRender != null || currIFrameConfig?.enabled}
        />
      )}

      {inputsLoading ? (
        <Skeleton />
      ) : rawInput ? (
        <div className="overflow-hidden rounded-lg border border-secondary">
          <CodeData
            value={inputs}
            diffValues={inputDiffs}
            onChange={onEditInputs}
            language="json"
            schema={schema}
            readOnly={!onEditInputs}
          />
        </div>
      ) : (
        <Stack gap={2}>
          <ErrorBoundary
            fallback={(error) => (
              <ErrorBoundaryRawFallback
                data={inputs}
                msg={`Failed to render the input, falling back to raw view: ${error.message}`}
                diffValues={inputDiffs}
                onChange={onEditInputs}
                readOnly={!onEditInputs}
                schema={schema}
              />
            )}
          >
            {onEditInputs ? (
              <EditableChatInput
                messages={inputMessages}
                setMessages={(input: MessageUnionType[]) => {
                  onEditInputs({ ...inputs, messages: input });
                }}
                preserveMessageFormat={true}
                messageFormat="oai"
                debounceUpdate
              />
            ) : (
              inputMessages?.map((message, index) => (
                <Message
                  key={index + ''}
                  testid={`run-input-component-chat-${index}`}
                  role={getMessageType(message) ?? 'HUMAN'}
                  content={getMessageContent(message) ?? ''}
                  attachments={run.inputs_s3_urls}
                  name={
                    getMessageFields(message)?.name ??
                    getMessageFields(message)?.role
                  }
                  fields={getMessageFields(message)}
                  onEditContent={handleEditInputMessage(
                    setMessageContent,
                    index
                  )}
                  onEditFields={handleEditInputMessage(setMessageFields, index)}
                  onChangeRole={handleEditInputMessage(setMessageType, index)}
                />
              ))
            )}
          </ErrorBoundary>
        </Stack>
      )}
    </>
  );
};

export const RunDetailsLLMOutput = ({
  outputs,
  outputsLoading,
  outputIndex,
  setOutputIndex,
  diffValues,
  forceRawRender,
  run,
  onEditOutputs,
  rawOutput,
  setRawOutput,
  hideHeaders,
  schema,
}: {
  outputs?: RunOutputSchema;
  outputsLoading?: boolean;
  outputIndex: number;
  setOutputIndex: React.Dispatch<React.SetStateAction<number>>;
  run: RunDisplaySchema;
  diffValues?: {
    decoration: DecorationSet;
    generatedCode: string | undefined;
  };
  forceRawRender?: 'raw' | 'rendered';
  onEditOutputs?: (outputs: RunSchema['outputs']) => void;
  rawOutput: boolean;
  setRawOutput: (value: boolean) => void;
  hideHeaders?: boolean;
  schema?: Record<string, unknown>;
}) => {
  const { currIFrameConfig } = useIFrameTraceRendering(
    run.reference_dataset_id
  );
  const generations = getFlattenedArrayOfGenerations(outputs);

  const outputMessagesWithIndex = getOutputMessagesWithIndex(generations);

  const outputMessages = outputMessagesWithIndex?.map(
    (m) => m?.[0] as MessageUnionType
  );

  const outputMessage = outputMessages?.[outputIndex];

  const onEditOutputMessage =
    onEditOutputs && outputs
      ? (newMessage: MessageUnionType) => {
          const updatedOutputs = updateMessageInOutputs(
            outputs,
            outputIndex,
            newMessage
          );
          onEditOutputs?.(updatedOutputs);
        }
      : undefined;

  const handleEditOutputMessage = (messageSetter) =>
    onEditOutputMessage
      ? (value) => {
          const newMessage = messageSetter(outputMessage, value);
          newMessage && onEditOutputMessage(newMessage);
        }
      : undefined;

  return (
    <>
      {!hideHeaders && (
        <RunDetailsHeaderToggle
          variant="output"
          raw={rawOutput}
          onRawChange={setRawOutput}
          disabled={!!diffValues}
          readOnly={forceRawRender != null}
          customIframe={currIFrameConfig?.enabled}
          disabledMessage="Turn off diff mode to change output view"
        />
      )}
      {outputsLoading ? (
        <Skeleton />
      ) : currIFrameConfig?.enabled && outputs && !rawOutput ? (
        <IFrameTraceRenderer
          config={currIFrameConfig}
          payload={outputs}
          type="output"
          key={`${run.id}-output`}
        />
      ) : rawOutput ? (
        <div className="overflow-hidden rounded-lg border border-secondary">
          <CodeData
            value={outputs}
            diffValues={diffValues}
            onChange={onEditOutputs}
            language="json"
            schema={schema}
            readOnly={!onEditOutputs}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <ErrorBoundary
            fallback={(error) => (
              <ErrorBoundaryRawFallback
                data={outputs}
                msg={`Failed to render the output, falling back to raw view: ${error.message}`}
                diffValues={diffValues}
                onChange={onEditOutputs}
                readOnly={!onEditOutputs}
                schema={schema}
              />
            )}
          >
            {outputMessage ? (
              <Message
                testid={`run-output-component-chat`}
                role={getMessageType(outputMessage) ?? 'AI'}
                content={getMessageContent(outputMessage) ?? ''}
                fields={getMessageFields(outputMessage)}
                attachments={run.outputs_s3_urls}
                onEditContent={handleEditOutputMessage(setMessageContent)}
                onEditFields={handleEditOutputMessage(setMessageFields)}
                onChangeRole={handleEditOutputMessage(setMessageType)}
                headerRight={
                  outputMessages &&
                  outputMessages.length > 1 && (
                    <ButtonGroup size="md">
                      <IconButton
                        icon={ChevronLeftIcon}
                        label="Previous output"
                        disabled={outputIndex === 0}
                        onClick={() => setOutputIndex((index) => index - 1)}
                      />
                      <IconButton
                        icon={ChevronRightIcon}
                        label="Next output"
                        disabled={outputIndex === outputMessages.length - 1}
                        onClick={() => setOutputIndex((index) => index + 1)}
                      />
                    </ButtonGroup>
                  )
                }
              />
            ) : (
              // If logging via API, they may have messages input with
              // arbitrary output
              <RunOutput
                outputs={outputs}
                attachments={run.outputs_s3_urls}
                onEditOutputs={onEditOutputs}
                runType={run.run_type}
              />
            )}
          </ErrorBoundary>
        </div>
      )}
    </>
  );
};
